import { FC, useContext, useEffect, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';
import { ITabOption, Tabs } from '@smartshell/ui-kit';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { AuthContext } from 'auth';
import { LocalizationContext } from 'localization';
import {
  GET_SHORTCUTS,
  IGetShortcutGroupsResponse,
  IGetShortcutsResponse,
  IShortcut,
  IShortcutGroup,
  SHORTCUT_GROUPS,
} from 'schemas';
import { ApplicationType } from 'types';

import s from './Applications.module.scss';
import Shortcuts from './components/Shortcuts';

export const Applications: FC = () => {
  const { t } = useContext(LocalizationContext);
  const {
    data: { permissions },
  } = useContext(AuthContext);

  const [appsType, setAppsType] = useState<ApplicationType>('GAME');
  const [selectedApp, setSelectedApp] = useState<IShortcut | null>(null);

  const [searchParams] = useSearchParams();
  const searchParamAppType = searchParams.get('type') as ApplicationType;
  const navigate = useNavigate();

  const { data, loading, refetch } = useQuery<IGetShortcutsResponse>(
    GET_SHORTCUTS,
    {
      fetchPolicy: 'no-cache',
      onCompleted: ({ shortcuts }) => {
        const selectedAppId = Number(searchParams.get('id'));

        const selectedShortcut = shortcuts.find(
          ({ id }) => id === selectedAppId
        );

        if (selectedShortcut) {
          setSelectedApp(selectedShortcut);
        }
      },
      skip: !permissions?.has('shortcut_browse'),
    }
  );

  const { data: shortcutGroups, loading: shortcutGroupsLoading } =
    useQuery<IGetShortcutGroupsResponse>(SHORTCUT_GROUPS, {
      fetchPolicy: 'no-cache',
      skip: !permissions?.has('shortcut_browse'),
    });

  useEffect(() => {
    if (!shortcutGroups) return;
  }, [shortcutGroups]);

  useEffect(() => {
    let url = `/applications?type=${appsType}`;

    if (selectedApp) {
      url += `&id=${selectedApp.id}`;
    }

    if (data?.shortcuts) {
      navigate(url, { replace: true });
    }
  }, [appsType, selectedApp, data?.shortcuts]);

  useEffect(() => {
    if (searchParamAppType === 'APPLICATION') {
      setAppsType(searchParamAppType);
    }
  }, [searchParamAppType]);

  useEffect(() => {
    if (selectedApp) {
      setSelectedApp(null);
    }
  }, [appsType]);

  const games = useMemo<IShortcut[]>(() => {
    return data?.shortcuts.filter((shortcut) => shortcut.type === 'GAME') ?? [];
  }, [data]);

  const apps = useMemo<IShortcut[]>(() => {
    return (
      data?.shortcuts.filter((shortcut) => shortcut.type === 'APPLICATION') ??
      []
    );
  }, [data]);

  const gameGroups = useMemo<IShortcutGroup[]>(
    () =>
      shortcutGroups?.shortcutGroups.filter(
        ({ groupType }) => groupType !== 'APPLICATION'
      ) || [],
    [shortcutGroups]
  );

  const appGroups = useMemo<IShortcutGroup[]>(
    () =>
      shortcutGroups?.shortcutGroups.filter(
        ({ groupType }) => groupType !== 'GAME'
      ) || [],
    [shortcutGroups]
  );

  const radioTabOptions: ITabOption<ApplicationType>[] = [
    {
      title: t('applications.games'),
      value: 'GAME',
      count: games.length,
    },
    {
      title: t('applications.apps'),
      value: 'APPLICATION',
      count: apps.length,
    },
  ];

  const loadingData = useMemo(
    () => !data || loading || shortcutGroupsLoading,
    [data, loading, shortcutGroupsLoading]
  );

  const OpenTabsRadio = (
    <Tabs
      value={appsType}
      onChange={setAppsType}
      options={radioTabOptions}
      itemVariant="row"
      className={s.tab}
    />
  );

  return (
    <div className={s['page-container']}>
      {appsType === 'GAME' && (
        <Shortcuts
          loadingData={loadingData}
          OpenTabRadio={OpenTabsRadio}
          shortcuts={games}
          refetch={refetch}
          selectedApp={selectedApp}
          setSelectedApp={setSelectedApp}
          groups={gameGroups.sort(({ sort: s1 }, { sort: s2 }) => s2 - s1)}
          size="l"
          placeholder={t('applications.games.default.placeholder')}
          groupPlaceholder={t('applications.games.groups.placeholder')}
          defaultGroupTitle={t('application.game.group.placeholder')}
          appType="GAME"
          linkTitle={t('applications.game.add')}
        />
      )}
      {appsType === 'APPLICATION' && (
        <Shortcuts
          loadingData={loadingData}
          OpenTabRadio={OpenTabsRadio}
          shortcuts={apps}
          refetch={refetch}
          selectedApp={selectedApp}
          setSelectedApp={setSelectedApp}
          groups={appGroups.sort(({ sort: s1 }, { sort: s2 }) => s2 - s1)}
          size="s"
          placeholder={t('applications.apps.default.placeholder')}
          groupPlaceholder={t('applications.apps.groups.placeholder')}
          defaultGroupTitle={t('application.app.group.placeholder')}
          appType="APPLICATION"
          linkTitle={t('applications.app.add')}
        />
      )}
    </div>
  );
};
